<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import type { JournalGroup } from '../journal/types'
import { useScreen } from '~/composables/useScreen'
import { currentUser, licensingUserId } from '~/lib/currentUser'

const props = defineProps<{ groups: JournalGroup[] }>()
const emit = defineEmits<{
  'open-student-cabinet': [groupId: number]
}>()

const { isMobile } = useScreen()

const openedUnitsList = ref(0) // value is group ID
const unitMenu = useTemplateRef<{ $openAt(t: EventTarget | null): void }>('unitMenu') // TODO: expose type as generic
const menuUnitId = ref(0) // value is junit ID

const menuUnit = computed(() =>
  props.groups.find(g => g.id === openedUnitsList.value)?.junits.find(u => u.id === menuUnitId.value)
)
const isMenuUnitCurrent = computed(
  () => props.groups.find(g => g.id === openedUnitsList.value)?.current_junit?.id === menuUnitId.value
)

function openUnitsList(groupId: number) {
  if (openedUnitsList.value === groupId) return (openedUnitsList.value = 0)
  else openedUnitsList.value = groupId
}

function getUnitsCount(group: JournalGroup) {
  const currentUnitIndex = group.junits.findIndex(u => u.id === group.current_junit?.id)
  return `${currentUnitIndex + 1 || group.junits.length} из ${group.junits.length}`
}
</script>

<template>
  <h2 class="text-body-bold mb-2">
    Мои группы
    <span class="gray-text ml-half" v-if="groups.length">{{ groups.length }}</span>
  </h2>

  <li v-for="group in groups.slice(0, 2)" :key="group.id" class="dashboard-group">
    <div class="d-flex align-center">
      <v-icon size="12" class="orange-text mr-2">mdi-checkbox-blank</v-icon>
      <h3 class="text-title-bold flex-spacer">
        {{ group.name }}
      </h3>

      <p v-if="!isMobile" class="dashboard-group__language-label" v-text="group.active_course.language_name" />
    </div>

    <p v-if="isMobile" class="dashboard-group__language-label mt-1" v-text="group.active_course.language_name" />

    <ul class="dashboard-group__card mt-3" :class="{ 'flex-column': isMobile }">
      <li class="flex-spacer d-flex flex-column">
        <span class="gray-text">Учеников</span>
        <span class="text-body-bold mt-1 flex-spacer" v-text="group.jstudents.length" />

        <span v-if="group.jstudents.length === 1" class="blue-text mt-2 text-button">
          <v-button
            v-if="currentUser.id === licensingUserId"
            :href="`/student/impersonate/${group.jstudents[0].student_id}`"
            target="_blank"
            mode="primary small"
            class="d-inline-flex"
          >
            Кабинет ученика
          </v-button>
          <a v-else :href="`/student/impersonate/${group.jstudents[0].student_id}`" target="_blank">
            Кабинет ученика
          </a>
        </span>

        <span v-else class="blue-text mt-2 text-button">
          <v-button
            v-if="currentUser.id === licensingUserId"
            @click="emit('open-student-cabinet', group.id)"
            mode="primary small"
          >
            Кабинет ученика
          </v-button>
          <a v-else @click="emit('open-student-cabinet', group.id)">Кабинет ученика</a>
        </span>
      </li>
      <li class="flex-spacer d-flex flex-column">
        <span class="gray-text">Курс</span>
        <span class="text-body-bold mt-1 flex-spacer" v-text="group.active_course.name" />
        <span class="blue-text mt-2 text-button">
          <a
            :href="`/journal/performance?group_id=${group.id}`"
            target="_blank"
            :class="currentUser.id === licensingUserId ? 'my-1 d-flex' : ''"
          >
            Открыть группу
          </a>
        </span>
      </li>
      <li class="flex-spacer d-flex flex-column">
        <span class="gray-text">Текущий юнит</span>
        <span class="text-body-bold mt-1 flex-spacer" v-text="getUnitsCount(group)" />
        <span class="blue-text mt-2 text-button">
          <v-button
            v-if="currentUser.id === licensingUserId"
            :href="`/courses/teacher_materials/course/${group.active_course.id}`"
            mode="primary small"
            class="d-inline-flex"
          >
            Материалы курса
          </v-button>
          <a v-else :href="`/courses/teacher_materials/course/${group.active_course.id}`">Материалы курса</a>
        </span>
      </li>
    </ul>

    <div class="dashboard-group__card flex-column mt-1">
      <a role="button" class="full-width d-flex justify-space-between align-center" @click="openUnitsList(group.id)">
        Структура курса:
        <v-icon key="1" v-if="openedUnitsList === group.id">mdi-chevron-up</v-icon>
        <v-icon key="0" v-else>mdi-chevron-down</v-icon>
      </a>

      <template v-if="openedUnitsList === group.id">
        <ul class="dashboard-group__units-list">
          <li
            v-for="(unit, i) in group.junits"
            :key="unit.id"
            class="dashboard-group__unit"
            :class="{
              'dashboard-group__unit_finished': unit.finished,
              'dashboard-group__unit_current': group.current_junit?.id === unit.id,
              'dashboard-group__unit_active': menuUnitId === unit.id
            }"
            @click="
              e => {
                unitMenu?.$openAt(e.currentTarget)
                menuUnitId = unit.id
              }
            "
          >
            U{{ i + 1 }}
          </li>
        </ul>

        <p class="text-caption gray-text">Нажмите на юнит для перехода к материалам или журналу</p>
      </template>
    </div>
  </li>

  <li v-if="groups.length > 2" class="d-flex">
    <v-button mode="primary outlined" href="/journal/groups" class="mb-4"> Перейти к списку групп </v-button>
  </li>

  <v-menu-base
    ref="unitMenu"
    @switch="
      (v: boolean) => {
        if (!v) menuUnitId = 0
      }
    "
  >
    <ul class="pa-1" v-if="menuUnit">
      <li>
        <p class="text-button gray-text pa-1">
          {{ menuUnit.name }}
          <span v-if="menuUnit.finished">· Завершён</span>
          <span v-if="isMenuUnitCurrent">· Текущий юнит</span>
        </p>
      </li>
      <li><v-menu-divider /></li>
      <li>
        <v-menu-button :href="`/courses/teacher_materials/unit/${menuUnit.unit_id}`" target="_blank">
          <v-icon class="mr-2">mdi-bookshelf</v-icon>
          Материалы юнита
        </v-menu-button>
      </li>
      <li>
        <v-menu-button
          :href="`/journal/performance?group_id=${openedUnitsList}&unit_id=${menuUnit.id}`"
          target="_blank"
        >
          <v-icon class="mr-2">mdi-school</v-icon>
          Открыть в Журнале
        </v-menu-button>
      </li>
    </ul>
  </v-menu-base>
</template>

<style lang="scss" scoped>
.dashboard-group {
  display: grid;
  background: $lightenGray;
  padding: 24px;
  border-radius: 24px;
  margin-bottom: 16px;

  @include mobile {
    padding: 16px;
    border-radius: 16px;
  }

  &__language-label {
    color: $darkGray;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  &__card {
    display: flex;
    gap: 16px;

    background: white;
    border-radius: 12px;
    padding: 16px;
  }

  &__units-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(48px, 1fr));
    gap: 4px;
  }

  &__unit {
    text-align: center;
    border-radius: 8px;
    width: 100%;
    height: 40px;
    background: $lightenGray;

    display: flex;
    align-items: center;
    justify-content: center;

    cursor: pointer;
    transition: border 0.3s ease;
    border: 2px solid transparent;

    &:hover,
    &_active {
      border: 2px solid $blue;
    }

    &_finished {
      background: $lightGreen;
    }

    &_current {
      background: $lightenBlue;
    }
  }
}
</style>
